import { peekCache, writeCache } from '../cache/queryCache';
import { getCachedGymId } from '../receptionist/gymContext';
import { branchScopeCacheSuffix } from '../gym/branchContext';

const TABLE_SUFFIX = {
  members: ['members:v2'],
  memberships: ['memberships:v2'],
  payments: ['payments:v3'],
  attendance_logs: ['attendance'],
  guest_passes: ['guest_passes'],
  membership_freezes: ['freezes'],
  lockers: ['lockers'],
  trainers: ['trainers'],
  equipments: ['equipments'],
  classes: ['classes'],
  receptionists: ['receptionists'],
  packages: ['packages'],
  branches: ['branches', 'branch-names'],
  expenses: ['finance-ledger:v5'],
};

function cacheKeysForTable(table) {
  const gid = getCachedGymId();
  if (!gid) return [];
  const suffixes = TABLE_SUFFIX[table] || [];
  const scope = branchScopeCacheSuffix();
  const keys = [];
  suffixes.forEach((suffix) => {
    keys.push(`gym:${gid}:${suffix}${scope}`);
    keys.push(`gym:${gid}:${suffix}`);
  });
  return keys;
}

function rowKey(row) {
  return String(row?.id || row?.payment_ref || row?.client_op_id || '');
}

function mergeIntoList(current, row) {
  if (current == null) return current;
  const list = Array.isArray(current) ? current : current.rows || current.items;
  if (!Array.isArray(list)) return current;

  const key = rowKey(row);
  const next = key
    ? list.filter((item) => rowKey(item) !== key)
    : list.slice();
  next.unshift({ ...row, __offline: true });

  if (Array.isArray(current)) return next;
  if (current.rows) return { ...current, rows: next, total: (current.total || next.length) };
  if (current.items) return { ...current, items: next, total: (current.total || next.length) };
  return current;
}

function removeFromList(current, match) {
  if (current == null) return current;
  const list = Array.isArray(current) ? current : current.rows || current.items;
  if (!Array.isArray(list)) return current;

  const next = list.filter((item) => {
    return !Object.entries(match || {}).every(([k, v]) => String(item?.[k] ?? '') === String(v ?? ''));
  });

  if (Array.isArray(current)) return next;
  if (current.rows) return { ...current, rows: next, total: Math.max(0, (current.total || next.length) - 1) };
  if (current.items) return { ...current, items: next, total: Math.max(0, (current.total || next.length) - 1) };
  return current;
}

function patchList(current, match, payload) {
  if (current == null) return current;
  const list = Array.isArray(current) ? current : current.rows || current.items;
  if (!Array.isArray(list)) return current;

  const next = list.map((item) => {
    const hit = Object.entries(match || {}).every(([k, v]) => String(item?.[k] ?? '') === String(v ?? ''));
    return hit ? { ...item, ...payload, __offline: true } : item;
  });

  if (Array.isArray(current)) return next;
  if (current.rows) return { ...current, rows: next };
  if (current.items) return { ...current, items: next };
  return current;
}

export function mergeOptimisticInsert(table, row) {
  cacheKeysForTable(table).forEach((key) => {
    const current = peekCache(key, { allowStale: true });
    if (current == null) return;
    writeCache(key, mergeIntoList(current, row));
  });
}

export function mergeOptimisticUpdate(table, match, payload) {
  cacheKeysForTable(table).forEach((key) => {
    const current = peekCache(key, { allowStale: true });
    if (current == null) return;
    writeCache(key, patchList(current, match, payload));
  });
}

export function mergeOptimisticDelete(table, match) {
  cacheKeysForTable(table).forEach((key) => {
    const current = peekCache(key, { allowStale: true });
    if (current == null) return;
    writeCache(key, removeFromList(current, match));
  });
}
